import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Backdrop,
  Divider,
} from "@mui/material";

// Import components
import KPISection from "../components/KPISection";
import GraduateSummaryCards from "../components/GraduateSummaryCards";
import GenderCards from "../components/GenderCards";
import GeneralChartsSection from "../components/GeneralChartsSection";
import EmploymentCharts from "../components/EmploymentCharts";
import DemographicsCharts from "../components/DemographicsCharts";
import TrendsCharts from "../components/TrendsCharts";

// Import services
import DashboardAPI from "../services/DashboardAPI";

const MinisterialDashboard = () => {
  // State management
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newHiresTrend, setNewHiresTrend] = useState([]);
  const [exitsTrend, setExitsTrend] = useState([]);
  const [filters, setFilters] = useState({
    yearRange: [2020, 2024],
    qualificationLevel: [],
    universities: [],
    yearOfCompletion: [],
    ageRange: [20, 50],
    workExperience: [0, 20],
    countryOfStudy: [],
    gender: [],
    fieldOfStudy: [],
    employmentStatus: [],
    sector: [],
  });

  // Load dashboard data on component mount and when filters change
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await DashboardAPI.fetchDashboardData(filters);
        setDashboardData(data);
      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [filters]);

   useEffect(() => {
  const loadTrendData = async () => {
    try {
      setLoading(true);
      const selectedYear = filters?.year || new Date().getFullYear();

      const hires = await DashboardAPI.getNewHiresTrend({ year: selectedYear });
      const exits = await DashboardAPI.getExitsTrend({ year: selectedYear });

      setNewHiresTrend(hires);
      setExitsTrend(exits);
    } catch (error) {
      console.error("❌ Error loading trend data:", error);
    } finally {
      setLoading(false);
    }
  };

  loadTrendData();
}, [filters]); 


  // Destructure dashboard data
  const {
    kpiGraduateData = [],
    kpiEmploymentData = [],
    educationLevelData = [],
    countryStudyData = [],
    employmentByFieldData = [],
    graduatesByGender = [],
    youthGraduatesByGender = [],
    genderEmploymentData = [],
    genderUnemploymentData = [],
    ageDistributionData = [],
    genderByFieldData = [],
    generalChartData = [],
    entryExitData = [],
  } = dashboardData;

  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error Message */}
      {error && (
        <Paper
          sx={{ p: 2, mb: 3, bgcolor: "#ffebee", border: "1px solid #f44336" }}
        >
          <Typography variant="body2" color="#d32f2f">
            ⚠️ Error: {error}
          </Typography>
        </Paper>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: "#1976d2", color: "white" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Skills Dashboard: Higher Education & Employment (Data from
              IPPIS)
            </Typography>
            <Typography variant="subtitle1">
              Comprehensive overview of graduate outcomes and employment trends
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Data Source Disclaimer */}
      <Paper
        sx={{ p: 2, mb: 3, bgcolor: "#e3f2fd", border: "1px solid #2196f3" }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "#1565c0", mr: 1 }}
          >
            ℹ️ Data Source Information:
          </Typography>
          <Typography variant="body2" color="#1565c0">
            This dashboard displays data sourced exclusively from the Integrated
            Personnel and Payroll Information System (IPPIS). These figures
            represent only a subset of the country's total education and
            employment landscape and should not be interpreted as comprehensive
            national statistics. The data covers IPPIS-registered personnel and
            graduates only.
          </Typography>
        </Box>
      </Paper>

      {/* Graduate Statistics KPIs */}
      {/* <KPISection
        title="Graduate Statistics"
        data={kpiGraduateData}
        color="#1976d2"
        icon=""
      /> */}

      {/* Employment Statistics KPIs */}
      <KPISection
        title="Public Servants Statistics"
        data={kpiEmploymentData}
        color="#388e3c"
        icon=""
      />

      {/* Graduate Summary Cards */}
      <GraduateSummaryCards
        educationLevelData={educationLevelData}
        countryStudyData={countryStudyData}
        employmentByFieldData={employmentByFieldData}
        useEmploymentRate={true}
      />

      {/* Gender Cards */}
      {/* <GenderCards
        graduatesByGender={graduatesByGender}
        youthGraduatesByGender={youthGraduatesByGender}
        genderEmploymentData={genderEmploymentData}
        genderUnemploymentData={genderUnemploymentData}
      /> */}

      {/* General Charts Section */}
      <GeneralChartsSection chartData={generalChartData} />

      {/* Employment Charts */}
      {/* <EmploymentCharts
        educationLevelData={educationLevelData}
        employmentByFieldData={employmentByFieldData}
      /> */}

      {/* Demographics Charts */}
      <DemographicsCharts
        ageDistributionData={ageDistributionData}
        genderByFieldData={genderByFieldData}
        entryExitData={entryExitData}
      />

       <TrendsCharts
        newHiresTrend={newHiresTrend}
        exitsTrend={exitsTrend}
        year={filters.year}
       />


      {/* Footer Description Card */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: "#f8f9fa", border: "1px solid #e9ecef" }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" gutterBottom sx={{ color: "#495057", fontWeight: "bold" }}>
            📊 Dashboard Overview
          </Typography>
          <Typography variant="body1" sx={{ color: "#6c757d", mb: 2, lineHeight: 1.6 }}>
            This comprehensive skills dashboard provides insights into higher education outcomes and employment trends 
            within the Integrated Personnel and Payroll Information System (IPPIS). The data visualizations help 
            stakeholders understand workforce demographics, field of study distributions, and employment patterns 
            across different age groups and sectors.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", mt: 2 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                Age Distribution
              </Typography>
              <Typography variant="caption" sx={{ color: "#6c757d" }}>
                Workforce composition by age groups
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#388e3c", fontWeight: "bold" }}>
                Entry & Exit Trends
              </Typography>
              <Typography variant="caption" sx={{ color: "#6c757d" }}>
                Employee movement by field of study
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#f57c00", fontWeight: "bold" }}>
                Gender Balance
              </Typography>
              <Typography variant="caption" sx={{ color: "#6c757d" }}>
                Gender distribution across fields
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ color: "#6c757d", fontStyle: "italic" }}>
            Last updated: {new Date().toLocaleDateString()} | Data source: IPPIS | 
            For technical support, contact the system administrator
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  // Add any required state mappings
});

export default connect(mapStateToProps)(MinisterialDashboard);

